import { SHOPIFY_STORE_DOMAIN, SHOPIFY_STOREFRONT_API_TOKEN } from '@portal/config/base';
import { reshapeToFormattedShopifyCollection } from '@portal/shared/utils/shopify/formatters/reshapeToFormattedShopifyCollection';
import { FormattedShopifyCollection } from '@portal/types/shopify/product.formatted.types';
import { StoreCode } from '../../../schema/storeCode';
import { TAGS } from '../constants';
import { getCollectionByIdQuery } from '../queries/collectionById';
import { getCollection } from '../queries/collectionsByHandleV2';
import { Product, ShopifyCollectionOperation } from '../types';
import { reshapeProducts, shopifyFetch } from '../utils';

async function getCollectionById({
  domain,
  token,
  collectionId
}: {
  domain: string;
  token: string;
  collectionId: string;
}): Promise<Product[]> {
  const res = await shopifyFetch<ShopifyCollectionOperation>({
    query: getCollectionByIdQuery,
    tags: [TAGS.collections],
    shopifyDomain: domain,
    accessToken: token,
    variables: {
      id: `gid://shopify/Collection/${collectionId}`
    }
  });

  return reshapeProducts(res.body.data.collection.products.nodes);
}

async function getCollectionByHandle({
  domain,
  token,
  handle,
  identifiers = []
}: {
  handle: string;
  identifiers?: { key: string; namespace: string }[];
  domain: string;
  token: string;
}): Promise<FormattedShopifyCollection | null> {
  const res = await shopifyFetch<any>({
    query: getCollection,
    tags: [TAGS.collections, TAGS.products],
    shopifyDomain: domain,
    accessToken: token,
    variables: { handle, identifiers }
  });

  if (res.body.data.collectionByHandle) {
    return reshapeToFormattedShopifyCollection(res.body.data.collectionByHandle);
  }

  return null;
}

// use this load function if you wish to return collection by id
export const loadShopifyCollectionById = async (
  storeCode: StoreCode,
  collectionId: string
): Promise<Product[]> => {
  const collection = await getCollectionById({
    domain: SHOPIFY_STORE_DOMAIN[storeCode],
    token: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
    collectionId
  });

  return collection;
};

// use this load function if you wish to return collection by handle
export const loadShopifyCollectionByHandle = async (
  storeCode: StoreCode,
  handle: string,
  identifiers?: { key: string; namespace: string }[]
): Promise<FormattedShopifyCollection | null> => {
  const collection = await getCollectionByHandle({
    domain: SHOPIFY_STORE_DOMAIN[storeCode],
    token: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
    handle,
    identifiers
  });

  return collection;
};

// use this load function if you wish to fetch multiple collections by their handles
export const loadMultipleShopifyCollectionsByHandles = async (
  storeCode: StoreCode,
  handles: string[],
  identifiers?: { key: string; namespace: string }[]
): Promise<(FormattedShopifyCollection | null)[]> => {
  const collectionsPromise = handles.map((handle) =>
    getCollectionByHandle({
      domain: SHOPIFY_STORE_DOMAIN[storeCode],
      token: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
      handle,
      ...(identifiers && { identifiers })
    })
  );

  const collections = await Promise.all(collectionsPromise);

  return collections;
};
